import { useCallback, useState } from 'react';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import {
  getWorkingProjectSessions,
  selectIsSingleSession,
  selectMainEventBanner,
  selectWorkingProjectHomepage,
  selectWorkingProjectImage,
  selectWorkingProjectRegistrationSettings,
} from '@/store/slices/project/selectors';
import { saveWorkingProjectByFields, saveWorkingProjectSessionByField } from '@/store/slices/project';
import ProjectImageCarousel from '@/micros/shared-create-and-edit-components/grid-areas/main/project-overview/project-image-carousel';
import { PageModuleType, type PageModule } from '@/types/module.types';

const defaultImage = 'https://assets.brandlive.com/assets/template-previews/Apollo-Speakers-default_image.jpeg';

const EditProjectImage = () => {
  const sessions = useAppSelector(getWorkingProjectSessions);
  const session = sessions?.[0];
  const sessionImage = session?.pre_broadcast_thumbnail;
  const workingProjectImage = useAppSelector(selectWorkingProjectImage);
  const regSettings = useAppSelector(selectWorkingProjectRegistrationSettings);
  const isSingleSession = useAppSelector(selectIsSingleSession);
  const mainEventBanner = useAppSelector(selectMainEventBanner);
  const homepage = useAppSelector(selectWorkingProjectHomepage);
  const bannerImage = mainEventBanner?.content.image !== defaultImage ? mainEventBanner?.content.image : undefined;
  const [uploadedProjectImage, setUploadedProjectImage] = useState<string | null | undefined>(workingProjectImage);
  const [uploadedSessionOrBannerImage, setUploadedSessionOrBannerImage] = useState<string | null | undefined>(
    isSingleSession ? sessionImage : bannerImage
  );
  const [uploadedRegistrationImage, setUploadedRegistrationImage] = useState<string | undefined>(regSettings?.image);
  const dispatch = useAppDispatch();

  const onUpload = useCallback(
    async (key: string, url: string | null) => {
      if (!session) return;

      // See https://brandlive.atlassian.net/browse/BDEV-2278 for AC details
      switch (key) {
        case 'projectImage': {
          setUploadedProjectImage(url);
          dispatch(saveWorkingProjectByFields({ event_image: url || undefined }));

          // if single session, match what shows in the session overview modal
          if (isSingleSession) {
            dispatch(
              saveWorkingProjectSessionByField({
                uuid: session.uuid,
                image: url,
              })
            );
          }
          return;
        }
        case 'sessionOrBannerImage': {
          setUploadedSessionOrBannerImage(url);

          // session image is the pre_broadcast_thumbnail (what shows in the prelive state)
          // NOT the session.image property, which should match the event image if this is a single session event
          if (isSingleSession) {
            dispatch(
              saveWorkingProjectSessionByField({
                uuid: session.uuid,
                pre_broadcast_thumbnail: url,
              })
            );

            // if this is NOT a single session event, then this image should be the main event banner image
          } else if (homepage && mainEventBanner) {
            dispatch(
              saveWorkingProjectByFields({
                homepage: {
                  ...homepage,
                  modules: homepage.modules.map(withUpdatedBannerImage(url)),
                  post_register_home_modules: homepage.post_register_home_modules.map(withUpdatedBannerImage(url)),
                },
              })
            );
          }
          return;
        }
        case 'registrationImage': {
          setUploadedRegistrationImage(url ?? undefined);
          dispatch(
            saveWorkingProjectByFields({
              registration_settings: regSettings
                ? {
                    ...regSettings,
                    image: url ?? undefined,
                  }
                : undefined,
            })
          );
          return;
        }
      }
    },
    [dispatch, homepage, isSingleSession, mainEventBanner, regSettings, session]
  );

  return (
    <ProjectImageCarousel
      onUpload={onUpload}
      onDelete={(key: string) => onUpload(key, null)}
      isSingleSession={isSingleSession}
      images={{
        projectImage: uploadedProjectImage,
        registrationImage: uploadedRegistrationImage,
        sessionOrBannerImage: uploadedSessionOrBannerImage,
      }}
    />
  );
};

const withUpdatedBannerImage =
  (url: string | null | undefined) =>
  (module: PageModule): PageModule => {
    if (module.type === PageModuleType.main_event_banner) {
      return {
        ...module,
        content: {
          ...module.content,
          image: url,
          originalImage: url,
        },
      };
    }

    return module;
  };

export default EditProjectImage;
